import { Api } from "./Api";

const ENDPOINTS = {
    History: "recouv/history",
    ExportExcel: "recouv/history/export_excel",
    StatsOverview: "recouv/historystats/overview",
    StatsDaily: "recouv/historystats/daily",
    StatsTopKeywords: "recouv/historystats/top_keywords",
    StatsTopUsers: "recouv/historystats/top_users",
    StatsRecentActivity: "recouv/historystats/recent_activity",
    StatsSearchResults: "recouv/historystats/search_results",
    StatsTypeDistribution: "recouv/historystats/type_distribution",
};

// Types disponibles pour les filtres
const HISTORY_TYPES = {
    NOTIFICATION: 'notification',
    API: 'api',
    SEARCH: 'search',
    CHATBOT: 'chatbot',
    DOCUMENT_CONSTITUTIF: 'document_constitutif',
    DECISION_JUSTICE: 'decision_justice',
};

/**
 * Construit l'URL avec les paramètres de recherche
 * @param {string} endpoint
 * @param {object} params
 * @returns {string}
 */
const buildUrlWithParams = (endpoint, params = {}) => {
    const queryParts = [];
    Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
            // Pour le paramètre 'type', on n'encode pas la virgule
            if (key === 'type' && typeof params[key] === 'string' && params[key].includes(',')) {
                queryParts.push(`${key}=${params[key]}`);
            } else {
                queryParts.push(`${key}=${encodeURIComponent(params[key])}`);
            }
        }
    });
    const queryString = queryParts.join('&');
    return queryString ? `${endpoint}?${queryString}` : endpoint;
};

/**
 * Récupère la liste paginée des historiques avec filtres optionnels
 * @param {object} filters - Filtres optionnels
 * @param {string} filters.company - UUID de l'entreprise
 * @param {string} filters.type - Type d'action (notification, api, search, chatbot, document_constitutif, decision_justice)
 * @param {string} filters.source - Source de l'action
 * @param {string} filters.request - Recherche dans la requête
 * @param {number} filters.page - Numéro de page
 * @param {number} filters.page_size - Taille de la page
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getHistory = (filters = {}, signal) => {
    const url = buildUrlWithParams(ENDPOINTS.History, filters);
    return Api.get(url, signal);
};

/**
 * Récupère l'historique des recherches de décisions de justice
 * @param {object} filters - Filtres optionnels (company, source, request, page, page_size)
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getDecisionsJusticeHistory = (filters = {}, signal) => {
    return getHistory({
        ...filters,
        type: HISTORY_TYPES.DECISION_JUSTICE,
    }, signal);
};

/**
 * Récupère l'historique des conversations chatbot sur décisions de justice
 * @param {object} filters - Filtres optionnels
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getChatbotDecisionsHistory = (filters = {}, signal) => {
    return getHistory({
        ...filters,
        type: HISTORY_TYPES.CHATBOT,
    }, signal);
};

/**
 * Exporte les historiques en fichier Excel
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const exportExcel = (signal) => {
    return Api.get(ENDPOINTS.ExportExcel, signal);
};

/**
 * Récupère les statistiques globales (overview)
 * @param {object} filters - Filtres optionnels
 * @param {string} filters.company - UUID de l'entreprise
 * @param {string} filters.type - Type d'action
 * @param {string} filters.source - Source
 * @param {string} filters.user_id - UUID de l'utilisateur
 * @param {string} filters.start_date - Date de début (YYYY-MM-DD)
 * @param {string} filters.end_date - Date de fin (YYYY-MM-DD)
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getStatsOverview = (filters = {}, signal) => {
    const url = buildUrlWithParams(ENDPOINTS.StatsOverview, filters);
    return Api.get(url, signal);
};

/**
 * Récupère les statistiques journalières
 * @param {number} days - Nombre de jours (défaut: 30)
 * @param {object} filters - Filtres additionnels
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getStatsDaily = (days = 30, filters = {}, signal) => {
    const url = buildUrlWithParams(ENDPOINTS.StatsDaily, { days, ...filters });
    return Api.get(url, signal);
};

/**
 * Récupère les mots-clés les plus recherchés
 * @param {number} limit - Nombre de résultats (défaut: 5)
 * @param {object} filters - Filtres additionnels
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getTopKeywords = (limit = 5, filters = {}, signal) => {
    const url = buildUrlWithParams(ENDPOINTS.StatsTopKeywords, { limit, ...filters });
    return Api.get(url, signal);
};

/**
 * Récupère les utilisateurs les plus actifs
 * @param {number} limit - Nombre de résultats (défaut: 5)
 * @param {object} filters - Filtres additionnels
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getTopUsers = (limit = 5, filters = {}, signal) => {
    const url = buildUrlWithParams(ENDPOINTS.StatsTopUsers, { limit, ...filters });
    return Api.get(url, signal);
};

/**
 * Récupère l'activité récente
 * @param {number} limit - Nombre de résultats (défaut: 20)
 * @param {object} filters - Filtres additionnels
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getRecentActivity = (limit = 20, filters = {}, signal) => {
    const url = buildUrlWithParams(ENDPOINTS.StatsRecentActivity, { limit, ...filters });
    return Api.get(url, signal);
};

/**
 * Récupère les statistiques des résultats de recherche
 * @param {object} filters - Filtres optionnels
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getSearchResults = (filters = {}, signal) => {
    const url = buildUrlWithParams(ENDPOINTS.StatsSearchResults, filters);
    return Api.get(url, signal);
};

/**
 * Récupère la distribution par type
 * @param {object} filters - Filtres optionnels
 * @param {AbortSignal} signal
 * @returns {Promise}
 */
const getTypeDistribution = (filters = {}, signal) => {
    const url = buildUrlWithParams(ENDPOINTS.StatsTypeDistribution, filters);
    return Api.get(url, signal);
};

export const HistoriqueService = {
    // Constantes
    TYPES: HISTORY_TYPES,

    // Historiques
    getHistory,
    getDecisionsJusticeHistory,
    getChatbotDecisionsHistory,
    exportExcel,

    // Statistiques
    getStatsOverview,
    getStatsDaily,
    getTopKeywords,
    getTopUsers,
    getRecentActivity,
    getSearchResults,
    getTypeDistribution,
};